import moment from 'moment'
import { del, get, patch, post, put } from './base'
import qs from 'qs'
import axios, { AxiosError, AxiosResponse, CancelToken } from 'axios'
import { snakeCase, sortBy, upperFirst } from 'lodash'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, getCurrentInstance, isRef, unref } from 'vue'
import { RECEIVE_JOB_JSON } from '@/store/modules/jobs'
import { LineItem } from '@/models/line-item'
import { JobEvent } from '@/models/job-event'
import { MaybeRef } from '@vueuse/core'
import JobReference from '@/models/job-reference'
import { FilterJobRequest } from '@/api/types/filter-job-request'
import { currentTimezone } from '@/utils/date-formatting.js'
import { toIsoString, type IsoDateLike } from '@/utils/date-helpers'
import JobComment from '@/models/job-comment'
import NewTag from '@/models/new_tag'
import { PriceBookItemQueryResponse } from '@/api/types/priceBookTypes'
import PriceBookItem from '@/models/price-book-item'
import {
  ActualStop,
  type CreateJobCommentRequestJson,
  type ActualStopReadJson,
  type ActualStopWriteJson,
  type InventoryItem,
  InventoryReceiptType,
  type InventoryReceiptWriteJson,
  JobRequest,
  type JobRequestJson,
  type JobCommentJson,
  JobStopComment,
  type JobStopCommentJson,
  JobStopType,
  RequestedStop,
  type RequestedStopReadJson,
  RequestedStopWrite,
  type RequestedStopWriteJson,
} from '@ironsight/types'
import { v4 as uuidv4 } from 'uuid'
import JobHistory, { JobHistoryJson } from '@/models/job-history'
import { JobsQuerySource } from '@/components/jobs/job-search-page/useFilterJobsQuery'
import { JobRequestJobsResponse } from '@/models/types/job-request-jobs-response'

const v3JobsEndpoint = '/api/v3/jobs'
const v4JobsEndpoint = '/api/v4/jobs'
const v5JobsScheduleEndpoint = '/api/v5/jobs/schedule'
const v5JobsEndpoint = '/api/v5/jobs'
const v6JobsEndpoint = '/api/v6/jobs'
const v5BackgroundWorkEndpoint = '/api/v5/background-work'

let getJobsCancelToken = axios.CancelToken.source()

export function getJobs({
  start,
  end,
  modifiedSince,
  divisions,
  resourceTypes,
  statuses,
  includeOngoing,
  includeArchived = false,
}: {
  start?: moment.Moment
  end?: moment.Moment
  modifiedSince?: moment.Moment
  divisions?: []
  resourceTypes?: []
  statuses?: []
  includeOngoing?: boolean
  includeArchived?: boolean
}) {
  getJobsCancelToken.cancel('Cancelling get jobs request because a new request is in progress.')
  getJobsCancelToken = axios.CancelToken.source()

  return get(v5JobsScheduleEndpoint, {
    cancelToken: getJobsCancelToken.token,
    params: {
      start: start && moment(start).toISOString(),
      end: end && moment(end).toISOString(),
      modifiedSince: modifiedSince && moment(modifiedSince).toISOString(),
      d: divisions,
      rt: resourceTypes,
      statuses,
      includeOngoing,
      includeArchived,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat' })
    },
  })
}

function jobSelectionRequest(
  filters: FilterJobRequest[],
  sortBy: string,
  sortDesc: boolean,
  timeZone: string,
) {
  return { filters, sort_by: sortBy, sort_desc: sortDesc, time_zone: timeZone }
}

export function filterJobs(
  filters: FilterJobRequest[],
  sortBy: string,
  sortDesc: boolean,
  timeZone: string,
  offset = 0,
  limit = 100,
  bucketId: string,
  signal?: AbortSignal,
  source: JobsQuerySource = 'direct',
) {
  const endpoint =
    bucketId.toLowerCase() === 'all'
      ? `${v5JobsEndpoint}/buckets/all/query`
      : `${v5JobsEndpoint}/buckets/${encodeURIComponent(bucketId)}/query`

  return post(endpoint, {
    data: {
      ...jobSelectionRequest(filters, sortBy, sortDesc, timeZone),
      offset,
      limit,
    },
    config: {
      signal,
      params: {
        source,
      },
    },
  })
}

export function filterJobsCount(
  filters: FilterJobRequest[],
  bucketId: string,
  timeZone: string,
  signal?: AbortSignal,
  limitCheck = 10000,
): Promise<AxiosResponse<{ count: number }>> {
  const bucketKey = bucketId.toLowerCase()
  return postJobBucketCounts(
    [
      {
        bucket_id: bucketKey === 'all' ? 'All' : upperFirst(bucketId),
        filters,
      },
    ],
    timeZone,
    signal,
    // All historically sends an explicit zero for server validation; other buckets omit it.
    bucketKey === 'all' ? limitCheck : limitCheck || undefined,
  ).then((response) => ({
    ...response,
    data: {
      count: response.data?.[bucketKey] ?? 0,
    },
  }))
}

/**
 * Fetches counts for multiple bucket IDs and sums them into a single count.
 */
export function filterJobsCountByBucketIds(
  filters: FilterJobRequest[],
  bucketIds: string[],
  timeZone: string,
  signal?: AbortSignal,
  limitCheck = 10000,
): Promise<AxiosResponse<{ count: number }>> {
  return filterJobsBucketCounts(
    bucketIds.map((id) => ({
      bucket_id: id,
      filters,
    })),
    timeZone,
    signal,
    limitCheck,
  ).then((response) => ({
    ...response,
    data: {
      count: Object.values(response.data ?? {}).reduce(
        (sum: number, c) => sum + (typeof c === 'number' ? c : 0),
        0,
      ),
    },
  }))
}

export type BucketCountsRequest = {
  bucket_id: string
  filters: FilterJobRequest[]
}

export type BucketCountsResponse = Record<string, number>

export function filterJobsBucketCounts(
  buckets: BucketCountsRequest[],
  timeZone: string,
  signal?: AbortSignal,
  limitCheck?: number,
) {
  return postJobBucketCounts(buckets, timeZone, signal, limitCheck || undefined)
}

function postJobBucketCounts(
  buckets: BucketCountsRequest[],
  timeZone: string,
  signal?: AbortSignal,
  limitCheck?: number,
) {
  return post(`${v5JobsEndpoint}/buckets/counts`, {
    data: {
      buckets,
      time_zone: timeZone,
      ...(limitCheck !== undefined ? { limit_check: limitCheck } : {}),
    },
    config: {
      signal,
    },
  })
}

export function searchJobs(params, cancelToken: CancelToken) {
  return post(v6JobsEndpoint + '/search', {
    data: Object.fromEntries(Object.entries(params).map(([k, v]) => [snakeCase(k), v])),
    config: {
      cancelToken,
    },
  })
}

export function searchJobsCount(params, cancelToken: CancelToken) {
  return post(v6JobsEndpoint + '/search/count', {
    data: Object.fromEntries(Object.entries(params).map(([k, v]) => [snakeCase(k), v])),
    config: {
      cancelToken,
    },
  })
}

export function rescheduleGroup(
  groupId: string,
  {
    offset,
    resourceId,
    serviceProviderId,
  }: {
    offset: string
    resourceId?: number | null
    serviceProviderId?: string | null
  },
) {
  return post(`${v3JobsEndpoint}/group/${groupId}/reschedule`, {
    data: {
      offset,
      resource_id: resourceId,
      service_provider_id: serviceProviderId,
    },
  })
}

export function groupJobs(jobIds: string[]) {
  return put(`${v3JobsEndpoint}/group`, {
    data: jobIds,
  })
}

export function deleteAssignmentGroup(groupId: string) {
  return del(`${v3JobsEndpoint}/group/${groupId}`)
}

export function addJobToAssignmentGroup(groupId: string, jobIdAdd: string) {
  return put(`${v3JobsEndpoint}/group/${groupId}/jobs/${jobIdAdd}`)
}

export function removeJobFromAssignmentGroup(groupId: string, jobIdToRemove: string) {
  return del(`${v3JobsEndpoint}/group/${groupId}/jobs/${jobIdToRemove}`)
}

export function exportFilteredJobs(
  filters: FilterJobRequest[],
  sortBy: string,
  sortDesc: boolean,
  timeZone: string,
  columns: string[],
  bucketIds: string[],
  format = 'csv',
  selectedJobIds: string[] = [],
) {
  return post(`${v5JobsEndpoint}/buckets/export`, {
    data: {
      ...jobSelectionRequest(filters, sortBy, sortDesc, timeZone),
      columns,
      bucket_ids: bucketIds,
      ...(selectedJobIds.length > 0 ? { selected_job_ids: selectedJobIds } : {}),
    },
    config: {
      responseType: 'blob',
      params: { format },
    },
  })
}

export type BackgroundWorkStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export interface BackgroundWorkItemResponse {
  id: string
  type: string
  name: string
  status: BackgroundWorkStatus
  created_at: string
  updated_at: string
  started_at?: string | null
  completed_at?: string | null
  total_item_count?: number | null
  completed_item_count: number
  has_download: boolean
  result_file_name?: string | null
  result_content_type?: string | null
  result_item_count?: number | null
  error_message?: string | null
}

export interface BackgroundWorkItemDownloadResponse {
  download_url: string
  file_name: string
  content_type: string
  item_count?: number | null
}

export function exportBulkPdfZip(
  filters: FilterJobRequest[],
  sortBy: string,
  sortDesc: boolean,
  timeZone: string,
  bucketIds: string[],
  selectedJobIds: string[] = [],
  pdfFormatId?: string,
): Promise<AxiosResponse<BackgroundWorkItemResponse>> {
  return post(`${v5JobsEndpoint}/bulk-pdf/export`, {
    data: {
      ...jobSelectionRequest(filters, sortBy, sortDesc, timeZone),
      bucket_ids: bucketIds,
      ...(selectedJobIds.length > 0 ? { selected_job_ids: selectedJobIds } : {}),
      ...(pdfFormatId ? { pdf_format_id: pdfFormatId } : {}),
    },
  })
}

export function getBackgroundWorkItem(
  workItemId: string,
): Promise<AxiosResponse<BackgroundWorkItemResponse>> {
  return get(`${v5BackgroundWorkEndpoint}/${workItemId}`) as Promise<
    AxiosResponse<BackgroundWorkItemResponse>
  >
}

export function getBackgroundWorkItemDownload(
  workItemId: string,
): Promise<AxiosResponse<BackgroundWorkItemDownloadResponse>> {
  return get(`${v5BackgroundWorkEndpoint}/${workItemId}/download`) as Promise<
    AxiosResponse<BackgroundWorkItemDownloadResponse>
  >
}

export function getJob(jobNumber: number) {
  return get(`${v6JobsEndpoint}/${jobNumber}?includeDestinationStop=true`, {})
}

export function getJobByUuid(id: string) {
  return get(`${v6JobsEndpoint}/${id}?includeDestinationStop=true`, {})
}

export function getJobPdf(jobId: string, pdfFormatId: string) {
  return get(`${v4JobsEndpoint}/${jobId}/pdf`, {
    params: {
      timezone: currentTimezone(),
      pdfFormat: pdfFormatId,
    },
    responseType: 'blob',
  })
}

export function getJobHistory(jobNumber: number, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const response = get<JobHistoryJson[]>(`${v3JobsEndpoint}/${jobNumber}/history`, {
    cancelToken: source.token,
  }).then((response) => {
    return response.data.map((history) => new JobHistory(history))
  })
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return response
}

export function createJobs(createJobsRequest, createProject: boolean) {
  return post(v6JobsEndpoint, {
    data: createJobsRequest,
    config: {
      params: {
        createProject,
      },
    },
  })
}

export function useJobCommentsQuery(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  const queryKey = computed(() => ['jobs', 'byId', computedJobId.value, 'comments'])

  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await get<JobCommentJson[]>(
        `${v6JobsEndpoint}/${computedJobId.value}/comments`,
      )
      return response.data.map((comment) => new JobComment(comment))
    },
    enabled: computed(() => !!computedJobId.value),
  })
}

export type CreateJobCommentVariables = {
  jobId: string
  comment: string
}

export function useCreateJobCommentMutation() {
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, CreateJobCommentVariables>({
    mutationFn: async ({ jobId, comment }) => {
      const request: CreateJobCommentRequestJson = {
        id: uuidv4(),
        comment,
        submitted_on: moment.utc().toISOString(),
      }

      await post(`${v6JobsEndpoint}/${jobId}/comment`, { data: request })
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(['jobs', 'byId', variables.jobId, 'comments'])
    },
  })
}

export function rescheduleJob(
  id: number,
  startTime: Date,
  endTime: Date,
  unitId: number | null,
  serviceProviderBusinessUnitId: string | null,
) {
  return put(`${v3JobsEndpoint}/${id}/reschedule`, {
    data: {
      start_time: startTime,
      end_time: endTime,
      unit_id: unitId,
      service_provider_business_unit_id: serviceProviderBusinessUnitId,
    },
  })
}

export function updateJob(job) {
  return put(`${v6JobsEndpoint}/${job.id}`, {
    data: job,
  })
}

export function activateJob(jobNumber: number) {
  return post(`${v3JobsEndpoint}/${jobNumber}/activate`, {})
}

export function pauseJob(jobNumber: number) {
  return post(`${v3JobsEndpoint}/${jobNumber}/pause`, {})
}

export function completeJob(jobNumber: number) {
  return post(`${v3JobsEndpoint}/${jobNumber}/complete`, {})
}

export function cancelJob(jobNumber: number) {
  return post(`${v3JobsEndpoint}/${jobNumber}/cancel`, {})
}

export function unCancelJob(jobNumber: number) {
  return post(`${v5JobsEndpoint}/${jobNumber}/uncancel`, {})
}

export function unCompleteJob(jobNumber: number) {
  return post(`${v5JobsEndpoint}/${jobNumber}/uncomplete`, {})
}

export function archive(jobId: string) {
  return bulkArchive([jobId])
}

export function unarchive(id: string) {
  return post(`${v3JobsEndpoint}/${id}/unarchive`, {})
}

export function approveJob(jobNumber: number) {
  return post(`${v3JobsEndpoint}/${jobNumber}/approve`, {})
}

export function submitJobForApproval(uuid: string, emails: string[] | null = null) {
  return post(`${v3JobsEndpoint}/${uuid}/approval`, {
    data: {
      emails,
    },
  })
}

export function cancelJobApprovalRequest(uuid: string) {
  return post(`${v3JobsEndpoint}/${uuid}/approval/retract`, {})
}

export function rejectJobApproval(id: string, rejectionMessage: string) {
  return post(`${v3JobsEndpoint}/${id}/reject-approval`, {
    data: {
      rejection_message: rejectionMessage,
    },
  })
}

export function unapproveJob(jobNumber: number) {
  return post(`${v3JobsEndpoint}/${jobNumber}/unapprove`, {})
}

export function fetchJobAttachment(jobId: string, attachmentId: string) {
  return get(`${v5JobsEndpoint}/${jobId}/attachments/${attachmentId}`)
}

export function updatePriority(jobNumbers: number[], priority: number) {
  return put(`${v3JobsEndpoint}/priority`, {
    data: {
      priority,
      job_ids: jobNumbers,
    },
  })
}

export function bulkOverwriteSchedule(
  jobNumbers: number[],
  startTime: moment.Moment | null,
  endTime: moment.Moment | null,
) {
  return put(`${v3JobsEndpoint}/scheduled-time`, {
    data: {
      start_time: startTime,
      end_time: endTime,
      job_ids: jobNumbers,
    },
  })
}

export function bulkReschedule(jobNumbers: number[], duration: number) {
  return post(`${v3JobsEndpoint}/reschedule`, {
    data: {
      time_offset_seconds: duration,
      job_ids: jobNumbers,
    },
  })
}

export function bulkUpdateDestination(jobNumbers: number[], destinationId: number) {
  return put(`${v3JobsEndpoint}/destination`, {
    data: {
      destination_id: destinationId,
      job_ids: jobNumbers,
    },
  })
}

export function bulkUpdatePickupLocation(
  jobNumbers: number[],
  pickupLocationId: number | undefined,
) {
  return put(`${v3JobsEndpoint}/pickup-location`, {
    data: {
      pickup_location_id: pickupLocationId,
      job_ids: jobNumbers,
    },
  })
}

export function bulkUpdateCostCenter(jobNumbers: number[], costCenter: string) {
  return put(`${v3JobsEndpoint}/cost-center`, {
    data: {
      cost_center: costCenter,
      job_ids: jobNumbers,
    },
  })
}

export function bulkUpdateDetails(jobNumbers: number[], details: string) {
  return put(`${v3JobsEndpoint}/details`, {
    data: {
      details,
      job_ids: jobNumbers,
    },
  })
}

export function bulkCancel(jobNumbers: number[]) {
  return put(`${v3JobsEndpoint}/cancel`, {
    data: {
      job_ids: jobNumbers,
    },
  })
}

export function bulkArchive(jobIds: string[]) {
  return put(`${v3JobsEndpoint}/archive`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function bulkUpdateCustomField(
  jobIds: string[],
  customFieldKey: string,
  customFieldValue: any,
) {
  return put(`${v3JobsEndpoint}/custom-field`, {
    data: {
      job_ids: jobIds,
      custom_field_key: customFieldKey,
      custom_field_value: customFieldValue,
    },
  })
}

export async function bulkUpdateProject(jobIds: string[], projectId: string) {
  return put(`${v3JobsEndpoint}/project`, {
    data: {
      job_ids: jobIds,
      project_id: projectId,
    },
  })
}

export enum BulkUpdateTagsOperation {
  ADD = 0,
  REMOVE = 1,
  OVERWRITE = 2,
}

type BulkUpdateTagsRequest = {
  jobIds: string[]
  tags: string[]
  operation: BulkUpdateTagsOperation
}

export function bulkUpdateTags(request: BulkUpdateTagsRequest) {
  return put(`${v6JobsEndpoint}/tags`, {
    data: {
      job_ids: request.jobIds,
      tags: request.tags,
      operation: request.operation,
    },
  })
}

export function useBulkUpdateTags(onSuccessCallback?: (variables: BulkUpdateTagsRequest) => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: BulkUpdateTagsRequest) => bulkUpdateTags(request),
    onSuccess: async (_, variables: BulkUpdateTagsRequest) => {
      onSuccessCallback?.(variables)
      await Promise.all([
        ...variables.jobIds.map((jobId) => queryClient.invalidateQueries(['tags', 'jobs', jobId])),
        queryClient.invalidateQueries(['tags', 'search']),
      ])
    },
  })
}

export function sendEmailNotificationForJob(
  jobNumber: number,
  emails: Record<number, string[]>,
  userSids: string[],
) {
  return post(`${v3JobsEndpoint}/${jobNumber}/notify`, {
    data: {
      emails,
      user_sids: userSids,
    },
  })
}

export function attachFormSubmission(
  jobId: string,
  attachFormSubmissionRequest: {
    form_id: string
    submission_id: string
    created_at: string
  },
) {
  return post(`${v3JobsEndpoint}/${jobId}/formSubmissions`, {
    data: attachFormSubmissionRequest,
  })
}

export function attachFormSubmissionToStop(
  jobId: string,
  stopId: string,
  attachFormSubmissionRequest: {
    form_id: string
    submission_id: string
    created_at: string
  },
) {
  return post(`${v6JobsEndpoint}/${jobId}/stops/${stopId}/formSubmissions`, {
    data: attachFormSubmissionRequest,
  })
}

export function attachFormRequirementSubmission(
  jobId: string,
  formRequirementId: string,
  formSubmissionId: string,
) {
  return put(`${v4JobsEndpoint}/${jobId}/form-requirements/${formRequirementId}`, {
    data: {
      form_submission_id: formSubmissionId,
    },
  })
}

export function uploadAttachment(jobId: string, attachmentId: string, file: File) {
  return post(`${v5JobsEndpoint}/${jobId}/attachments/${attachmentId}`, {
    data: {
      attachment: file,
    },
    config: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  })
}

export function removeAttachment(jobId: string, attachmentId: string) {
  return del(`${v3JobsEndpoint}/${jobId}/attachments/${attachmentId}`)
}

export function updateCompletedJobActualTimes(
  jobId: string,
  updateCompletedJobActualTimesRequest: {
    start_time: string
    end_time: string
    active_time: string
    completed_time: string
  },
) {
  return put(`${v3JobsEndpoint}/${jobId}/actual-times`, {
    data: updateCompletedJobActualTimesRequest,
  })
}

export interface GetJobEventsQueryResponse {
  job_id: string
  event_id: string
  event_type: string
  timestamp: Date
  metadata: Record<string, any> | null
  data: Record<string, any> | null
}

export function getJobEventsQuery(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  return useQuery({
    queryKey: ['events', computedJobId.value],
    queryFn: async () => {
      const response = await get<GetJobEventsQueryResponse[]>(
        `${v3JobsEndpoint}/${computedJobId.value}/events`,
        {},
      )

      return response.data
    },
    select: (data) => data.map((event) => new JobEvent(event)),
  })
}

export function useAcceptJobMutation() {
  const queryClient = useQueryClient()
  const { isLoading, isError, error, isSuccess, mutate } = useMutation<AxiosResponse, AxiosError>({
    mutationFn: (jobNumber) => post(`${v3JobsEndpoint}/${jobNumber}/accept`),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['jobs'])
    },
  })

  return {
    isLoading,
    isError,
    error,
    isSuccess,
    accept: mutate,
  }
}

export function useDeclineJobMutation() {
  const queryClient = useQueryClient()
  const { isLoading, isError, error, isSuccess, mutate } = useMutation({
    mutationFn: (jobNumber) => post(`${v3JobsEndpoint}/${jobNumber}/decline`),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['jobs'])
    },
  })

  return {
    isLoading,
    isError,
    error,
    isSuccess,
    decline: mutate,
  }
}

export function useJobQuery(jobNumber: number) {
  const instance = getCurrentInstance()
  const enabled = computed(() => !isRef(jobNumber) || !!jobNumber.value)
  const { data, isLoading, error } = useQuery({
    queryKey: ['jobs', jobNumber],
    queryFn: () =>
      getJob(unref(jobNumber)).then((r) => {
        instance?.proxy?.$store.commit(`jobs/${RECEIVE_JOB_JSON}`, r)
        return r.data
      }),
    enabled,
    retry: (failureCount, error: AxiosError<{ detail: string }>) => {
      return !(failureCount > 2 || error.response?.status === 404)
    },
  })

  return { data, isLoading, error }
}

export function useBulkAssignMutation() {
  const queryClient = useQueryClient()
  const { isLoading, isError, error, isSuccess, mutateAsync } = useMutation({
    mutationFn: (variables: {
      jobIds: string[]
      resourceId?: number | null
      serviceProviderId?: string | null
      operators?: string[] | null
    }) =>
      put(`${v3JobsEndpoint}/assign`, {
        data: {
          job_ids: variables.jobIds,
          resource_id: variables.resourceId,
          service_provider_id: variables.serviceProviderId,
          operators: variables.operators,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['jobs'])
    },
  })

  return {
    isLoading,
    isError,
    error,
    isSuccess,
    bulkAssign: mutateAsync,
  }
}

export function useBulkUnassignMutation() {
  const queryClient = useQueryClient()
  const { isLoading, isError, error, isSuccess, mutateAsync } = useMutation({
    mutationFn: (variables: { jobIds: string[] }) =>
      put(`${v3JobsEndpoint}/unassign`, {
        data: { job_ids: variables.jobIds },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['jobs'])
    },
  })

  return {
    isLoading,
    isError,
    error,
    isSuccess,
    bulkUnassign: mutateAsync,
  }
}

/** Wire shape of a line item's "More details", in both directions. */
export interface LineItemDetailsJson {
  reference_number: string | null
  description: string | null
  worker_id: string | null
  /** A calendar date, `yyyy-MM-dd` on the way in. */
  work_date: string | null
  storage_location: string | null
  serial_number: string | null
}

export interface GetJobLineItemQueryResponse {
  id: string
  name: string
  rate: number | null
  quantity: number | null
  unit_of_measure: string | null
  price_book_item_id: string | null
  created: Date
  position: number | null
  matches_rate_agreement: boolean | null
  /** Absent from older backends; treat as billable. */
  billable?: boolean
  /** Only returned to the assigned provider's users; `null` for everyone else. */
  details?: LineItemDetailsJson | null
  /** A price book item type; the backend copies the price book item's onto linked items. */
  type?: string | null
}

export function getJobLineItemsQuery(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  return useQuery({
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    queryKey: ['line-items', computedJobId.value],
    queryFn: async () => {
      const response = await get<GetJobLineItemQueryResponse[]>(
        `${v3JobsEndpoint}/${computedJobId.value}/line-items`,
        {},
      )

      return response.data
    },
    select: (data) => {
      return sortBy(
        data.map((lineItem) => new LineItem(lineItem)),
        (lineItem) => lineItem.position ?? Infinity,
      )
    },
    retry: (failureCount, error: AxiosError) =>
      failureCount < 3 && error.status !== undefined && error.status >= 500,
  })
}

export function getJobPriceBookItems(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  return useQuery({
    queryKey: ['price-book-items', computedJobId.value],
    queryFn: async () => {
      const response = await get<PriceBookItemQueryResponse[]>(
        `${v6JobsEndpoint}/${computedJobId.value}/pricebook`,
        {},
      )

      return response.data
    },
    select: (data) => data.map((item) => new PriceBookItem(item)),
  })
}

export interface GetJobReferenceQueryResponse {
  id: number
  reference_type: string
  referenced_id: string | null
  url: string | null
}

export function useGetJobReferencesQuery(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  return useQuery({
    queryKey: ['references', computedJobId.value],
    queryFn: async () => {
      const response = await get<GetJobReferenceQueryResponse[]>(
        `${v5JobsEndpoint}/${computedJobId.value}/references`,
        {},
      )

      return response.data
    },
    select: (data) => data.map((reference) => new JobReference(reference)),
  })
}

interface JobLineItemRequest {
  id: string
  name: string | null
  rate?: number | null
  quantity?: number | null
  price_book_item_id?: string | null
  position: number | null
  type?: string | null
  /** Ignored by the backend unless the organization has the additional line item fields entitlement. */
  billable?: boolean
  /**
   * Replaces all of the item's details when present; omit to leave them as they are.
   * Ignored by the backend without the additional line item fields entitlement.
   */
  details?: LineItemDetailsJson
}

type BulkCreateAndUpdateJobLineItemMutationVariables = {
  jobId: string
  request: JobLineItemRequest[]
}

export function bulkCreateJobLineItemMutation({ disableOnSuccessCallBack = false }) {
  const queryClient = useQueryClient()
  return useMutation<AxiosResponse, AxiosError, BulkCreateAndUpdateJobLineItemMutationVariables>({
    mutationFn: ({ jobId, request }) => {
      return post(`${v6JobsEndpoint}/${jobId}/line-items`, {
        data: request,
      })
    },
    onSuccess: async (data, variables) => {
      if (!disableOnSuccessCallBack) {
        await Promise.all([
          queryClient.invalidateQueries(['line-items', variables.jobId]),
          queryClient.invalidateQueries(['invoice']),
        ])
      }
    },
  })
}

export function bulkUpdateJobLineItemMutation({ disableOnSuccessCallBack = false }) {
  const queryClient = useQueryClient()
  return useMutation<AxiosResponse, AxiosError, BulkCreateAndUpdateJobLineItemMutationVariables>({
    mutationFn: ({ jobId, request }) => {
      return patch(`${v6JobsEndpoint}/${jobId}/line-items`, {
        data: request,
      })
    },
    onSuccess: async (data, variables) => {
      if (!disableOnSuccessCallBack) {
        await Promise.all([
          queryClient.invalidateQueries(['line-items', variables.jobId]),
          queryClient.invalidateQueries(['invoice']),
        ])
      }
    },
  })
}

type BulkDeleteJobLineItemMutationVariables = {
  jobId: string
  lineItemIds: string[]
}

export function bulkDeleteJobLineItemMutation({ disableOnSuccessCallBack = false }) {
  const queryClient = useQueryClient()
  return useMutation<AxiosResponse, AxiosError, BulkDeleteJobLineItemMutationVariables>({
    mutationFn: ({ jobId, lineItemIds }) => {
      return del(`${v6JobsEndpoint}/${jobId}/line-items`, {
        data: lineItemIds,
      })
    },
    onSuccess: async (data, variables) => {
      if (!disableOnSuccessCallBack) {
        await Promise.all([
          queryClient.invalidateQueries(['line-items', variables.jobId]),
          queryClient.invalidateQueries(['invoice']),
        ])
      }
    },
  })
}

export function rejectJobApprovalMutation() {
  return useMutation({
    mutationFn: ({ id, rejectionMessage }: { id: string; rejectionMessage: string }) => {
      return rejectJobApproval(id, rejectionMessage)
    },
  })
}

export function submitJobForApprovalMutation() {
  return useMutation({
    mutationFn: ({ uuid, emails }: { uuid: string; emails: string[] | null }) => {
      return submitJobForApproval(uuid, emails)
    },
  })
}

export function useBulkArchiveMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobIds }: { jobIds: string[] }) => {
      return bulkArchive(jobIds)
    },
    onSuccess: async (_, variables) => {
      await Promise.all(
        variables.jobIds.map((jobId) => queryClient.invalidateQueries(['jobs', jobId])),
      )
    },
  })
}

export function useUnarchiveMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId }: { jobId: string }) => {
      return unarchive(jobId)
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries(['jobs', variables.jobId])
    },
  })
}

export function getJobTags(jobId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const tags = get<NewTag[]>(`${v6JobsEndpoint}/${jobId}/tags`).then((response) =>
    response.data.map((tag) => NewTag.fromJson(tag)),
  )
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return tags
}

export function useGetJobTagsQuery(jobId: MaybeRef<string | null | undefined>) {
  const computedJobId = computed(() => unref(jobId))
  const queryKey = computed(() => ['tags', 'jobs', computedJobId.value])
  const enabled = computed(() => !!computedJobId.value)
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getJobTags(computedJobId.value!, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    enabled,
  })
}

export async function createJobStop(
  jobId: string,
  locationId: number,
  stopType: JobStopType,
  requestedStopId: string | null = null,
  arrivedAt: IsoDateLike,
): Promise<string> {
  const response = await post(`${v6JobsEndpoint}/${jobId}/stops`, {
    data: {
      occurred_at: moment().utc().toISOString(),
      location_id: locationId,
      stop_type: stopType,
      requested_stop_id: requestedStopId,
      arrived_at: toIsoString(arrivedAt),
    } satisfies ActualStopWriteJson,
  })
  return await response.data
}

export async function updateJobStop(
  jobId: string,
  stopId: string,
  locationId: number,
  stopType: JobStopType,
  arrivedAt: IsoDateLike,
): Promise<string> {
  const response = await put(`${v6JobsEndpoint}/${jobId}/stops/${stopId}`, {
    data: {
      occurred_at: moment().utc().toISOString(),
      location_id: locationId,
      stop_type: stopType,
      arrived_at: toIsoString(arrivedAt),
    } satisfies ActualStopWriteJson,
  })
  return await response.data
}

export async function updateStopReceipts(
  jobId: string,
  stopId: string,
  receipts: InventoryItem[],
  occurredAt: IsoDateLike,
  receiptType: InventoryReceiptType,
) {
  const response = await post(`${v6JobsEndpoint}/${jobId}/stops/${stopId}/receipts`, {
    data: {
      occurred_at: toIsoString(occurredAt),
      receipts: receipts.map((r) => ({
        quantity: r.quantity,
        inventory_sub_type_id: r.inventorySubTypeId,
      })) satisfies InventoryReceiptWriteJson[],
      receipt_type: receiptType,
    },
  })
  return await response.data
}

export function useGetJobStopsQuery(jobId: MaybeRef<string>) {
  const queryClient = useQueryClient()
  const computedJobId = computed(() => unref(jobId))
  const computedQueryKey = computed(() => ['stops', 'byJobId', computedJobId.value])
  const enableQuery = computed(() => !!computedJobId.value)

  return useQuery({
    enabled: enableQuery,
    queryKey: computedQueryKey,
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    queryFn: async () => {
      const response = await get<ActualStopReadJson[]>(
        `${v6JobsEndpoint}/${computedJobId.value}/stops`,
        {},
      )

      return response.data
    },
    select: (data) =>
      data.map((s) => {
        const stop = new ActualStop(s)
        queryClient.setQueryData(['stops', 'byId', stop.id], stop)
        return stop
      }),
  })
}

export function useGetJobRequestedStopsQuery(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  const computedQueryKey = computed(() => ['requested-stops', 'byJobId', computedJobId.value])
  return useQuery({
    queryKey: computedQueryKey,
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    queryFn: async () => {
      const response = await get<RequestedStopReadJson[]>(
        `${v6JobsEndpoint}/${computedJobId.value}/requested-stops`,
        {},
      )

      return response.data
    },
    select: (data) =>
      data.map((s) => {
        return new RequestedStop(s)
      }),
  })
}

export async function updateRequestedStops(jobId: string, requestedStops: RequestedStopWrite[]) {
  const response = await post(`${v6JobsEndpoint}/${jobId}/requested-stops`, {
    data: {
      requested_stops: requestedStops.map((rs) => ({
        id: rs.id,
        location_id: rs.locationId,
        position: rs.position,
        stop_type: rs.stopType,
        requested_inventory: rs.inventory.map((ri) => ({
          id: ri.id,
          quantity: ri.quantity,
          inventory_sub_type_id: ri.inventorySubTypeId,
        })),
      })) satisfies RequestedStopWriteJson[],
    },
  })
  return await response.data
}

export async function skipRequestedStop(
  jobId: string,
  stopId: string,
  occurredAt: IsoDateLike,
  skipComment?: string,
) {
  const response = await post(`${v6JobsEndpoint}/${jobId}/requested-stops/${stopId}/skip`, {
    data: {
      occurred_at: toIsoString(occurredAt),
      skip_comment: skipComment,
    },
  })
  return await response.data
}

export function deleteActualStop(jobId: string, stopId: string) {
  return del(`${v6JobsEndpoint}/${jobId}/stops/${stopId}`)
}

export function getJobStopById(stopId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const response = get<ActualStopReadJson>(`${v6JobsEndpoint}/stops/${stopId}`, {
    cancelToken: source.token,
  }).then((response) => {
    const s = response.data
    return new ActualStop(s)
  })
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return response
}

export function useGetJobStopCommentsQuery(jobId: MaybeRef<string>) {
  const computedJobId = computed(() => unref(jobId))
  const queryKey = computed(() => ['stops', 'byJobId', computedJobId.value, 'comments'])
  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await get<JobStopCommentJson[]>(
        `${v6JobsEndpoint}/${computedJobId.value}/stops/comments`,
        {},
      )
      return response.data
    },
    select: (data) => data.map((comment) => new JobStopComment(comment)),
  })
}

export function useSkipRequestedStopMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      jobId,
      stopId,
      occurredAt,
      skipComment,
    }: {
      jobId: string
      stopId: string
      occurredAt: IsoDateLike
      skipComment?: string
    }) => {
      return skipRequestedStop(jobId, stopId, occurredAt, skipComment)
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries(['stops', 'byJobId', variables.jobId]),
        queryClient.invalidateQueries(['requested-stops', 'byJobId', variables.jobId]),
      ])
    },
  })
}

export function getJobRequest(jobRequestId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const response = get<JobRequestJson>(`${v6JobsEndpoint}/repeat_configuration/${jobRequestId}`, {
    cancelToken: source.token,
  }).then((response) => {
    return new JobRequest(response.data)
  })
  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return response
}

export function updateJobRequestConfiguration(
  parentJobId: string,
  maxExecutions: number,
  isRepeatTrip: boolean,
) {
  return patch(`${v6JobsEndpoint}/${parentJobId}/repeat_configuration`, {
    data: {
      max_executions: maxExecutions,
      is_repeat_trip: isRepeatTrip,
    },
  })
}

export function useGetJobRequestQuery(jobRequestId: MaybeRef<string>) {
  const computedJobRequestId = computed(() => unref(jobRequestId))
  const queryKey = computed(() => ['job-request', 'byId', computedJobRequestId.value])
  const enabled = computed(() => !!computedJobRequestId.value)
  return useQuery({
    queryKey,
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    queryFn: ({ signal }) => getJobRequest(computedJobRequestId.value!, signal),
    enabled,
  })
}

export async function getJobRequestJobs(jobRequestId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()
  const response = await get<JobRequestJobsResponse[]>(
    `${v6JobsEndpoint}/requests/${jobRequestId}/jobs`,
    {
      cancelToken: source.token,
    },
  )

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })
  return response.data
}

export function useGetJobRequestJobsQuery(jobRequestId: MaybeRef<string>) {
  const queryClient = useQueryClient()
  const computedJobRequestId = computed(() => unref(jobRequestId))
  const queryKey = computed(() => ['job-request', 'jobsById', computedJobRequestId.value])
  const enabled = computed(() => !!computedJobRequestId.value)
  return useQuery({
    queryKey,
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    queryFn: async ({ signal }) => await getJobRequestJobs(computedJobRequestId.value!, signal),
    enabled,
    select: (data: JobRequestJobsResponse[]) => {
      for (const jobData of data) {
        queryClient.setQueryData(
          ['requested-stops', 'byJobId', jobData.id],
          jobData.requested_stops,
        )
        for (const stop of jobData.actual_stops) {
          queryClient.setQueryData(['stops', 'byId', stop.id], stop)
        }
        queryClient.setQueryData(['stops', 'byJobId', jobData.id], jobData.actual_stops)
        queryClient.setQueryData(['line-items', jobData.id], jobData.line_items)
      }

      return data
    },
  })
}

export function bulkApprove(jobIds: string[]) {
  return put(`${v6JobsEndpoint}/approve`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function bulkUnapprove(jobIds: string[]) {
  return put(`${v6JobsEndpoint}/unapprove`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function bulkReject(jobIds: string[], rejectionMessage: string) {
  return put(`${v6JobsEndpoint}/reject`, {
    data: {
      job_ids: jobIds,
      rejection_message: rejectionMessage,
    },
  })
}

export function bulkSubmitForApproval(jobIds: string[]) {
  return put(`${v6JobsEndpoint}/approval`, {
    data: {
      job_ids: jobIds,
    },
  })
}

export function useBulkApproveMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobIds }: { jobIds: string[] }) => {
      return bulkApprove(jobIds)
    },
    onSuccess: async (_, variables) => {
      await Promise.all(
        variables.jobIds.map((jobId) => queryClient.invalidateQueries(['jobs', jobId])),
      )
    },
  })
}

export function useBulkUnapproveMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobIds }: { jobIds: string[] }) => {
      return bulkUnapprove(jobIds)
    },
    onSuccess: async (_, variables) => {
      await Promise.all(
        variables.jobIds.map((jobId) => queryClient.invalidateQueries(['jobs', jobId])),
      )
    },
  })
}

export function useBulkRejectMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobIds, rejectionMessage }: { jobIds: string[]; rejectionMessage: string }) => {
      return bulkReject(jobIds, rejectionMessage)
    },
    onSuccess: async (_, variables) => {
      await Promise.all(
        variables.jobIds.map((jobId) => queryClient.invalidateQueries(['jobs', jobId])),
      )
    },
  })
}

export function useBulkSubmitForApprovalMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ jobIds }: { jobIds: string[] }) => {
      return bulkSubmitForApproval(jobIds)
    },
    onSuccess: async (_, variables) => {
      await Promise.all(
        variables.jobIds.map((jobId) => queryClient.invalidateQueries(['jobs', jobId])),
      )
    },
  })
}
